<template>
  <div class="company-recruiter-action-column" :data-test="`recruiter-actions-${rowIndex}`">
    <WdsMenu
      z-index="50"
      placement="bottom-end"
      manual-trigger
      :global="tableGlobals"
    >
      <template #trigger="{ toggle }">
        <WdsButtonGhost
          :data-test="`company-recruiter-row-actions-${column.id}`"
          @click="toggle"
        >
          <WdsIcon name="overflow-menu-horizontal" :size="16" />
        </WdsButtonGhost>
      </template>

      <WdsMenuOption
        :data-test="`edit-recruiter-button-${rowIndex}`"
        @click="showEditModal = true"
      >
        <WdsIcon :size="16" :name="WdsIconNameEnum.EDIT" />
        {{ $t('Edit') }}
      </WdsMenuOption>

      <WdsMenuDivider />

      <WdsMenuOption
        theme="DESTRUCTIVE"
        :data-test="`delete-recruiter-button-${rowIndex}`"
        @click="showDeleteModal = true"
      >
        <WdsIcon :size="16" :name="WdsIconNameEnum.TRASH_CAN" />
        {{ $t('Delete') }}
      </WdsMenuOption>
    </WdsMenu>

    <teleport :to="tableGlobals">
      <CompanyRecruiterModal
        v-if="showEditModal"
        :show="showEditModal"
        :on-cancel="() => (showEditModal = false)"
        :on-confirm="onEditConfirm"
        :title="$t('Edit recruiter')"
        :name-disabled="true"
        :email-disabled="true"
        :message-disabled="true"
        :initial-name="column.recruiter.name"
        :initial-email="column.email ?? ''"
        :initial-fee="(column.recruiterFee ?? 0) * 100"
        :initial-ownership="column.recruiterOwnershipDays ?? undefined"
        :initial-ownership-never-expires="column.recruiterOwnershipDays === null"
        :initial-max-candidate-submissions="column.maxCandidateSubmissions ?? undefined"
        :initial-tags="column.tags.map((t) => t.name)"
        :initial-manages-workers="column.recruiterManagesWorkers"
        :manage-workers="column.recruiterManagesWorkers"
        :can-configure-recruiter-manages-workers="canConfigureRecruiterManagesWorkers"
        :has-hires="column.hasHires"
        :error-text="$t('An error occurred while trying to edit recruiter')"
      />

      <DeleteContact
        v-if="showDeleteModal"
        :confirm-message="$t('Are you sure you want to delete this recruiter?')"
        :confirm-button-text="$t('Yes, delete recruiter')"
        :on-confirm="onDeleteConfirm"
        :show="showDeleteModal"
        @cancel="showDeleteModal = false"
      />
    </teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, ref, type Ref } from 'vue'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsMenu,
  WdsMenuDivider,
  WdsMenuOption,
} from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { $t } from '@/composables/translation'
import CompanyRecruiterModal from '@/components/Contacts/CompanyRecruiter/CompanyRecruiterModal.vue'
import DeleteContact from '@/components/Contacts/DeleteContact.vue'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { UpdateCompanyRecruiterMutation } from '@/components/Contacts/CompanyRecruiter/mutations/UpdateCompanyRecruiterMutation'
import { DeleteCompanyRecruiterMutation } from '@/components/Contacts/CompanyRecruiter/mutations/DeleteCompanyRecruiterMutation'
import { useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'

const tableGlobals = useTableGlobalsSelector()

const CompanyRecruiterActionColumn = graphql(`
  fragment CompanyRecruiterActionColumn on CompanyRecruiter {
    id
    email
    recruiterFee
    recruiterOwnershipDays
    maxCandidateSubmissions
    recruiterManagesWorkers
    hasHires
    recruiter {
      id
      name
    }
    tags {
      id
      name
    }
  }
`)

const props = defineProps<{
  row: FragmentType<typeof CompanyRecruiterActionColumn>
  rowIndex?: number
}>()
const column = computed(() => useFragment(CompanyRecruiterActionColumn, props.row))
const rowIndex = computed(() => props.rowIndex ?? 0)

const canConfigureRecruiterManagesWorkers = inject<Ref<boolean>>(
  'canConfigureRecruiterManagesWorkers',
  ref(false),
)

const showEditModal = ref(false)
const showDeleteModal = ref(false)

function refetchTable() {
  publicApi.refetchQueries({ include: ['CompanyRecruitersTableQuery'] })
}

function onEditConfirm(data: any) {
  return publicApi
    .mutate({
      mutation: UpdateCompanyRecruiterMutation,
      variables: {
        input: {
          id: column.value.id,
          recruiterFee: data.recruiterFee,
          recruiterOwnershipDays: data.recruiterOwnershipDays,
          maxCandidateSubmissions: data.maxCandidateSubmissions,
          tags: data.tags,
          ...(data.managesWorkers !== undefined && { managesWorkers: data.managesWorkers }),
        },
      },
    })
    .then((result) => {
      refetchTable()
      return result
    })
}

function onDeleteConfirm() {
  return publicApi
    .mutate({
      mutation: DeleteCompanyRecruiterMutation,
      variables: {
        input: {
          id: column.value.id,
        },
      },
    })
    .then((result) => {
      showDeleteModal.value = false
      refetchTable()
      return result
    })
}
</script>

<style scoped lang="scss">
.company-recruiter-action-column {
  :deep(.wds-menu) {
    width: max-content;
  }
}
</style>
